import React, { useState } from 'react';
import { PlusIcon, GripVerticalIcon, XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FormField } from './FormField';
export interface CaptureField {
  id: string;
  type: 'preset' | 'custom';
  preset?: 'name' | 'email' | 'phone' | 'address' | 'company';
  label?: string;
  description?: string;
  required: boolean;
}
interface CaptureFieldsEditorProps {
  fields: CaptureField[];
  onChange: (fields: CaptureField[]) => void;
}
export function CaptureFieldsEditor({
  fields,
  onChange
}: CaptureFieldsEditorProps) {
  const [newFieldType, setNewFieldType] = useState<'preset' | 'custom'>('preset');
  const [newFieldPreset, setNewFieldPreset] = useState<'name' | 'email' | 'phone' | 'address' | 'company'>('name');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const handleAddField = () => {
    if (newFieldType === 'preset') {
      // Check if this preset already exists
      const presetExists = fields.some(field => field.type === 'preset' && field.preset === newFieldPreset);
      if (presetExists) {
        alert(`A field for "${getPresetLabel(newFieldPreset)}" already exists.`);
        return;
      }
      const newField: CaptureField = {
        id: `preset-${newFieldPreset}-${Date.now()}`,
        type: 'preset',
        preset: newFieldPreset,
        required: newFieldRequired
      };
      onChange([...fields, newField]);
    } else if (newFieldType === 'custom') {
      if (!newFieldLabel.trim()) {
        alert('Please enter a label for the custom field.');
        return;
      }
      const newField: CaptureField = {
        id: `custom-${Date.now()}`,
        type: 'custom',
        label: newFieldLabel,
        description: newFieldDescription,
        required: newFieldRequired
      };
      onChange([...fields, newField]);
      setNewFieldLabel('');
      setNewFieldDescription('');
    }
    setShowAddForm(false);
  };
  const handleRemoveField = (fieldId: string) => {
    onChange(fields.filter(field => field.id !== fieldId));
  };
  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === fields.length - 1) {
      return;
    }
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newFields[index];
    newFields[index] = newFields[targetIndex];
    newFields[targetIndex] = temp;
    onChange(newFields);
  };
  const handleToggleRequired = (fieldId: string) => {
    onChange(fields.map(field => field.id === fieldId ? {
      ...field,
      required: !field.required
    } : field));
  };
  const getPresetLabel = (preset: 'name' | 'email' | 'phone' | 'address' | 'company'): string => {
    const labels = {
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      address: 'Billing Address',
      company: 'Company Name'
    };
    return labels[preset];
  };
  return <div className="space-y-4" data-id="element-1798">
      <div className="flex justify-between items-center mb-2" data-id="element-1799">
        <h3 className="text-sm font-medium text-gray-700" data-id="element-1800">
          Customer Information Fields
        </h3>
        <button type="button" onClick={() => setShowAddForm(!showAddForm)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-1801">
          {showAddForm ? 'Cancel' : <>
              <PlusIcon size={14} className="mr-1" data-id="element-1802" />
              Add Field
            </>}
        </button>
      </div>
      {/* Add new field form */}
      {showAddForm && <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4" data-id="element-1803">
          <div className="mb-3" data-id="element-1804">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1805">
              Field Type
            </label>
            <div className="flex space-x-3" data-id="element-1806">
              <label className="flex items-center" data-id="element-1807">
                <input type="radio" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={newFieldType === 'preset'} onChange={() => setNewFieldType('preset')} data-id="element-1808" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-1809">Preset Field</span>
              </label>
              <label className="flex items-center" data-id="element-1810">
                <input type="radio" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={newFieldType === 'custom'} onChange={() => setNewFieldType('custom')} data-id="element-1811" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-1812">Custom Field</span>
              </label>
            </div>
          </div>
          {newFieldType === 'preset' ? <div className="mb-3" data-id="element-1813">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1814">
                Preset Field
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={newFieldPreset} onChange={e => setNewFieldPreset(e.target.value as any)} data-id="element-1815">
                <option value="name" data-id="element-1816">Full Name</option>
                <option value="email" data-id="element-1817">Email Address</option>
                <option value="phone" data-id="element-1818">Phone Number</option>
                <option value="address" data-id="element-1819">Billing Address</option>
                <option value="company" data-id="element-1820">Company Name</option>
              </select>
            </div> : <>
              <div className="mb-3" data-id="element-1821">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1822">
                  Field Label
                </label>
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Project Name" value={newFieldLabel} onChange={e => setNewFieldLabel(e.target.value)} data-id="element-1823" />
              </div>
              <div className="mb-3" data-id="element-1824">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1825">
                  Field Description (Optional)
                </label>
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Please provide the project name for your invoice" value={newFieldDescription} onChange={e => setNewFieldDescription(e.target.value)} data-id="element-1826" />
              </div>
            </>}
          <div className="mb-3" data-id="element-1827">
            <label className="flex items-center" data-id="element-1828">
              <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={newFieldRequired} onChange={e => setNewFieldRequired(e.target.checked)} data-id="element-1829" />
              <span className="ml-2 text-sm text-gray-700" data-id="element-1830">Required field</span>
            </label>
          </div>
          <div className="flex justify-end" data-id="element-1831">
            <button type="button" onClick={handleAddField} className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm" data-id="element-1832">
              Add Field
            </button>
          </div>
        </div>}
      {/* List of fields */}
      {fields.length > 0 ? <div className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-1833">
          {fields.map((field, index) => <div key={field.id} className="p-3 flex items-center" data-id="element-1834">
              <div className="flex-shrink-0 mr-2 text-gray-400" data-id="element-1835">
                <GripVerticalIcon size={16} data-id="element-1836" />
              </div>
              <div className="flex-1" data-id="element-1837">
                <div className="flex justify-between items-center" data-id="element-1838">
                  <div data-id="element-1839">
                    <span className="font-medium text-gray-800" data-id="element-1840">
                      {field.type === 'preset' ? getPresetLabel(field.preset!) : field.label}
                    </span>
                    {field.required && <span className="ml-1 text-red-500 text-xs" data-id="element-1841">*</span>}
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-1842">
                    <button type="button" onClick={() => handleToggleRequired(field.id)} className={`text-xs px-2 py-1 rounded ${field.required ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`} data-id="element-1843">
                      {field.required ? 'Required' : 'Optional'}
                    </button>
                    <button type="button" onClick={() => handleMoveField(index, 'up')} disabled={index === 0} className={`text-gray-400 hover:text-gray-600 ${index === 0 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1844">
                      <ChevronUpIcon size={16} data-id="element-1845" />
                    </button>
                    <button type="button" onClick={() => handleMoveField(index, 'down')} disabled={index === fields.length - 1} className={`text-gray-400 hover:text-gray-600 ${index === fields.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1846">
                      <ChevronDownIcon size={16} data-id="element-1847" />
                    </button>
                    <button type="button" onClick={() => handleRemoveField(field.id)} className="text-gray-400 hover:text-red-500" data-id="element-1848">
                      <XIcon size={16} data-id="element-1849" />
                    </button>
                  </div>
                </div>
                {field.type === 'custom' && field.description && <p className="text-xs text-gray-500 mt-1" data-id="element-1850">
                    {field.description}
                  </p>}
                <p className="text-xs text-gray-500 mt-1" data-id="element-1851">
                  {field.type === 'preset' ? 'Standard input field' : 'Custom input field'}
                </p>
              </div>
            </div>)}
        </div> : <div className="text-center py-6 bg-gray-50 rounded-md border border-gray-200" data-id="element-1852">
          <p className="text-gray-500" data-id="element-1853">No capture fields added</p>
          <p className="text-sm text-gray-400 mt-1" data-id="element-1854">
            Add fields to collect information from your customers
          </p>
        </div>}
      <div className="text-xs text-gray-500 mt-2" data-id="element-1855">
        These fields will be shown on the payment page for customers to fill out
        before completing their payment.
      </div>
    </div>;
}